import {
  Home,
  ClipboardList,
  Users,
  MoreHorizontal,
  Sun,
  Plane,
  Clock,
  Fingerprint,
  Wallet,
  BookOpen,
  Briefcase,
  BarChart3,
  Settings,
} from "lucide-react";
import submenu from "../submenuConfig";
import {
  hasPermission,
  menuItemHasReadAccess,
} from "../../utils/permissionUtils";

/** Số mục tối đa mỗi nhóm nhật ký / cần xử lý trên hub mobile */
export const HUB_PREVIEW_LIMIT = 3;

/** Tab Công việc → dashboard mobile */
export const WORK_TAB_PATH = "/m/work";
/** Danh sách đầy đủ (desktop UI) khi cần xem chi tiết */
export const WORK_FULL_LIST_PATH = "/hrm/tasks";
export const WORK_FEATURE_CODE = "hrm.tasks";

/**
 * Ưu tiên chiếm slot 2 & 3 trên bottom nav.
 * Công việc (tasks) tách riêng HRM hub.
 */
export const PRIMARY_SLOT_PRIORITY = [
  "work",
  "crm",
  "finance",
  "elearning",
  "hrm",
];

export const MODULE_LABELS = {
  crm: "CRM (Khách hàng)",
  hrm: "HRM (Nhân sự)",
  elearning: "E-Learning",
  finance: "Tài chính",
  reports: "Báo cáo",
  settings: "Cài đặt (hệ thống)",
  work: "Công việc",
};

/** Thứ tự hiển thị trong /m/more → nhóm Module khác */
export const MORE_MODULE_ORDER = [
  "crm",
  "hrm",
  "elearning",
  "finance",
  "reports",
  "settings",
];

function isItemEnabled(item) {
  return item && item.enabled !== false;
}

export function getModuleHubItems(group, { excludeKeys = [] } = {}) {
  return (submenu[group] || [])
    .filter(isItemEnabled)
    .filter((item) => !excludeKeys.includes(item.key))
    .filter((item) => menuItemHasReadAccess(item))
    .map((item) => ({ ...item, group }));
}

export function getCrmHubItems() {
  return getModuleHubItems("crm");
}

/** HRM hub: không gồm tasks (đã có tab/dashboard Công việc) */
export function getHrmHubItems() {
  return getModuleHubItems("hrm", { excludeKeys: ["tasks"] });
}

export function getFinanceHubItems() {
  return getModuleHubItems("finance");
}

export function getElearningHubItems() {
  return getModuleHubItems("elearning");
}

/** Shortcut cards: drop dashboard + keys already covered by KPI cards */
export function filterHubShortcuts(items = [], excludeKeys = []) {
  const skip = new Set(["dashboard", ...excludeKeys]);
  return items.filter((item) => !skip.has(item.key));
}

/** Mobile CRM hub order: primary care first; dashboard last */
const CRM_HUB_PRIORITY = [
  "customers",
  "contact_history",
  "contracts",
  "documents",
  "teacher_accounts",
];

export function sortCrmHubItems(items = []) {
  const rank = (key) => {
    if (key === "dashboard") return 1000;
    const i = CRM_HUB_PRIORITY.indexOf(key);
    return i === -1 ? 500 : i;
  };
  return [...items].sort((a, b) => {
    const d = rank(a.key) - rank(b.key);
    if (d !== 0) return d;
    return String(a.label || "").localeCompare(String(b.label || ""), "vi");
  });
}

export function getSortedCrmHubItems() {
  return sortCrmHubItems(getCrmHubItems());
}

export function canShowWorkTab() {
  return hasPermission(WORK_FEATURE_CODE, "R");
}

export function canShowCrmTab() {
  return getCrmHubItems().length > 0;
}

export function canShowFinanceTab() {
  return getFinanceHubItems().length > 0;
}

export function canShowElearningTab() {
  return getElearningHubItems().length > 0;
}

export function canShowHrmTab() {
  return getHrmHubItems().length > 0;
}

const PRIMARY_CANDIDATES = {
  work: {
    key: "work",
    label: "Công việc",
    icon: ClipboardList,
    path: WORK_TAB_PATH,
    tone: "rose",
    canAccess: canShowWorkTab,
  },
  crm: {
    key: "crm",
    label: "CRM",
    icon: Users,
    path: "/m/crm",
    tone: "sky",
    canAccess: canShowCrmTab,
  },
  finance: {
    key: "finance",
    label: "Tài chính",
    icon: Wallet,
    path: "/m/finance",
    tone: "teal",
    canAccess: canShowFinanceTab,
  },
  elearning: {
    key: "elearning",
    label: "E-Learning",
    icon: BookOpen,
    path: "/m/elearning",
    tone: "indigo",
    canAccess: canShowElearningTab,
  },
  hrm: {
    key: "hrm",
    label: "HRM",
    icon: Briefcase,
    path: "/m/hrm",
    tone: "violet",
    canAccess: canShowHrmTab,
  },
  reports: {
    key: "reports",
    label: "Báo cáo",
    icon: BarChart3,
    path: "/m/reports",
    tone: "amber",
    canAccess: () => getModuleHubItems("reports").length > 0,
  },
  settings: {
    key: "settings",
    label: "Cài đặt",
    icon: Settings,
    path: null,
    tone: "slate",
    canAccess: () => getModuleHubItems("settings").length > 0,
  },
};

/** Tối đa 2 module chính theo thứ tự ưu tiên */
export function getPrimaryMobileModules(limit = 2) {
  const selected = [];
  for (const key of PRIMARY_SLOT_PRIORITY) {
    if (selected.length >= limit) break;
    const def = PRIMARY_CANDIDATES[key];
    if (def?.canAccess()) selected.push({ ...def });
  }
  return selected;
}

export function getPrimaryMobileModuleKeys() {
  return getPrimaryMobileModules(2).map((m) => m.key);
}

/**
 * Items trong Thêm → nhóm "Module khác".
 * Thứ tự cố định; bỏ mọi module đã có trên bottom Navigator.
 * Hub mobile (crm/hrm/elearning/finance/reports) → 1 dòng vào hub.
 * settings → 1 dòng tới chức năng đầu tiên còn quyền.
 */
export function getMoreModuleItems() {
  const primaryKeys = new Set(getPrimaryMobileModuleKeys());
  const hubPaths = {
    crm: "/m/crm",
    finance: "/m/finance",
    elearning: "/m/elearning",
    hrm: "/m/hrm",
    reports: "/m/reports",
  };

  return MORE_MODULE_ORDER.flatMap((group) => {
    if (primaryKeys.has(group)) return [];

    if (hubPaths[group]) {
      const hubItems =
        group === "hrm" ? getHrmHubItems() : getModuleHubItems(group);
      if (hubItems.length === 0) return [];
      return [
        {
          key: `${group}_hub`,
          label: MODULE_LABELS[group] || group,
          icon: PRIMARY_CANDIDATES[group]?.icon,
          group,
          path: hubPaths[group],
          isHub: true,
          tone: PRIMARY_CANDIDATES[group]?.tone,
        },
      ];
    }

    const accessible = getModuleHubItems(group);
    if (accessible.length === 0) return [];
    const first = accessible[0];
    return [
      {
        key: `${group}_hub`,
        label: MODULE_LABELS[group] || group,
        icon: PRIMARY_CANDIDATES[group]?.icon || first.icon,
        group,
        path: `/${group}/${first.key}`,
        isHub: true,
        tone: PRIMARY_CANDIDATES[group]?.tone,
      },
    ];
  });
}

/** Quick-create actions for FAB sheet */
export const QUICK_CREATE_ACTIONS = [
  {
    id: "task",
    label: "Công việc",
    icon: ClipboardList,
    path: "/hrm/tasks",
    permissionCode: "hrm.tasks",
  },
  {
    id: "on_leave",
    label: "Nghỉ phép",
    icon: Sun,
    path: "/hrm/on_leave",
    permissionCode: "hrm.on_leave",
  },
  {
    id: "overtime",
    label: "Tăng ca",
    icon: Clock,
    path: "/hrm/overtime",
    permissionCode: "hrm.overtime",
  },
  {
    id: "business_trip",
    label: "Công tác",
    icon: Plane,
    path: "/hrm/business_trip",
    permissionCode: "hrm.business_trip",
  },
  {
    id: "attendance",
    label: "Chấm công",
    icon: Fingerprint,
    path: "/hrm/hrm_attendance",
    permissionCode: "hrm.attendance",
  },
  // Khách hàng / Tạm ứng: form phức tạp — chỉ desktop, không đưa vào Tạo nhanh mobile
];

export function getVisibleQuickCreateActions() {
  return QUICK_CREATE_ACTIONS.filter((action) =>
    hasPermission(action.permissionCode, "C")
  );
}

export function canShowFab() {
  return getVisibleQuickCreateActions().length > 0;
}

export function getVisibleMobileTabs() {
  const home = {
    key: "home",
    label: "Tổng quan",
    icon: Home,
    path: "/m/home",
    always: true,
  };
  const more = {
    key: "more",
    label: "Thêm",
    icon: MoreHorizontal,
    path: "/m/more",
    always: true,
  };
  const primary = getPrimaryMobileModules(2);
  return [home, ...primary, more];
}

/** Split tabs for bar: left of FAB / right of FAB */
export function getMobileNavLayout() {
  const tabs = getVisibleMobileTabs();
  const showFab = canShowFab();
  const home = tabs.find((t) => t.key === "home");
  const more = tabs.find((t) => t.key === "more");
  const primary = tabs.filter((t) => t.key !== "home" && t.key !== "more");

  // Cân layout quanh FAB: [home + slot2?] | FAB | [slot3? + more]
  const left = [home, primary[0]].filter(Boolean);
  const right = [primary[1], more].filter(Boolean);

  return { left, right, showFab, tabs, primary };
}
